import { useGetOne, useGetList } from "ra-core";
import { Card, CardContent } from "@/components/ui/card";
import {
  ArrowLeft,
  HardHat,
  Mail,
  Phone,
  FileCheck,
  Shield,
  Calendar,
} from "lucide-react";
import { SubStatusBadge } from "./SubStatusBadge";
import { SubScorecard } from "./SubScorecard";

interface Sub {
  id: string;
  first_name: string;
  last_name: string;
  company_name: string;
  email: string | null;
  phone: string | null;
  specialties: string[];
  status: string;
  w9_on_file: boolean;
  w9_received_at: string | null;
  insurance_on_file: boolean;
  insurance_expiry: string | null;
  default_rate: number | null;
  rate_type: string;
  avg_rating: number;
  total_jobs: number;
  completed_jobs: number;
  on_time_pct: number;
  notes: string;
  created_at: string;
}

interface Assignment {
  id: string;
  job_id: string;
  status: string;
  role_description: string;
  rating: number | null;
  was_on_time: boolean | null;
  created_at: string;
}

interface SubDetailProps {
  subId: string;
  onBack: () => void;
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString("en-US", {
    month: "short", day: "numeric", year: "numeric",
  });
}

export function SubDetail({ subId, onBack }: SubDetailProps) {
  const { data: sub, isPending } = useGetOne<Sub>("subcontractors", { id: subId });
  const { data: assignments } = useGetList<Assignment>("job_assignments", {
    pagination: { page: 1, perPage: 50 },
    sort: { field: "created_at", order: "DESC" },
    filter: { subcontractor_id: subId },
  });

  if (isPending || !sub) {
    return <div className="py-8 text-center text-sm text-muted-foreground">Loading…</div>;
  }

  const name = `${sub.first_name} ${sub.last_name}`.trim() || sub.company_name;

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center gap-3">
        <button onClick={onBack} className="p-1.5 rounded hover:bg-gray-100">
          <ArrowLeft className="w-4 h-4" />
        </button>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <HardHat className="w-4 h-4 text-muted-foreground" />
            <SubStatusBadge status={sub.status} />
          </div>
          <h3 className="text-lg font-semibold truncate mt-0.5">{name}</h3>
          {sub.company_name && sub.first_name && (
            <p className="text-xs text-muted-foreground">{sub.company_name}</p>
          )}
        </div>
      </div>

      {/* Contact + compliance */}
      <Card>
        <CardContent className="p-4 space-y-2">
          <div className="flex flex-wrap gap-4 text-sm">
            {sub.phone && (
              <span className="flex items-center gap-1.5 text-muted-foreground">
                <Phone className="w-3.5 h-3.5" /> {sub.phone}
              </span>
            )}
            {sub.email && (
              <span className="flex items-center gap-1.5 text-muted-foreground">
                <Mail className="w-3.5 h-3.5" /> {sub.email}
              </span>
            )}
          </div>
          <div className="flex flex-wrap gap-3 text-xs">
            <span className="flex items-center gap-1" style={{
              color: sub.w9_on_file ? "#16a34a" : "#dc2626",
            }}>
              <FileCheck className="w-3 h-3" />
              W-9 {sub.w9_on_file ? "on file" : "missing"}
            </span>
            <span className="flex items-center gap-1" style={{
              color: sub.insurance_on_file ? "#16a34a" : "#dc2626",
            }}>
              <Shield className="w-3 h-3" />
              Insurance {sub.insurance_on_file ? "on file" : "missing"}
              {sub.insurance_expiry && ` (exp ${formatDate(sub.insurance_expiry)})`}
            </span>
          </div>
          {sub.specialties.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mt-1">
              {sub.specialties.map((s: string) => (
                <span key={s} className="text-xs bg-blue-50 text-blue-700 px-2 py-0.5 rounded-full">
                  {s}
                </span>
              ))}
            </div>
          )}
          {sub.default_rate && (
            <p className="text-xs text-muted-foreground">
              Default rate: ${sub.default_rate}/{sub.rate_type}
            </p>
          )}
        </CardContent>
      </Card>

      {/* Scorecard */}
      <SubScorecard
        avgRating={Number(sub.avg_rating)}
        totalJobs={sub.total_jobs}
        completedJobs={sub.completed_jobs}
        onTimePct={Number(sub.on_time_pct)}
      />

      {/* Assignment history */}
      <div>
        <h4 className="text-sm font-semibold mb-2">
          Assignment History ({assignments?.length || 0})
        </h4>
        {(!assignments || assignments.length === 0) && (
          <p className="text-sm text-muted-foreground">No assignments yet.</p>
        )}
        {assignments && assignments.length > 0 && (
          <div className="space-y-1.5">
            {assignments.map((a) => (
              <Card key={a.id} className="shadow-none">
                <CardContent className="p-2.5 flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium">
                      {a.role_description || "Assignment"}
                    </p>
                    <p className="text-xs text-muted-foreground flex items-center gap-1">
                      <Calendar className="w-3 h-3" />
                      {formatDate(a.created_at)}
                      <span className="ml-2 px-1.5 py-0.5 rounded text-xs" style={{
                        color: a.status === "completed" ? "#16a34a" : "#2563eb",
                        backgroundColor: a.status === "completed" ? "#dcfce7" : "#dbeafe",
                      }}>
                        {a.status}
                      </span>
                    </p>
                  </div>
                  {a.rating && (
                    <div className="text-right">
                      <p className="text-sm font-bold">{Number(a.rating).toFixed(1)}</p>
                      <p className="text-xs text-muted-foreground">rating</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
